import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Logo, useTagline } from "../components/Logo";
import { Mail, ArrowRight, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [err, setErr] = useState("");
  const tagline = useTagline();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", {
        email,
        origin_url: window.location.origin,
      });
      setDone(true);
      if (data?.mocked && data?.reset_link) setResetLink(data.reset_link);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 ym-grid-bg opacity-30" />
        <Logo inverted />
        <div className="relative">
          <h2 className="font-heading font-black text-4xl leading-tight">
            Don't worry,<br />we've got your back.
          </h2>
          <p className="mt-4 text-blue-100 italic">"{tagline}"</p>
        </div>
        <div className="relative text-blue-100 text-sm">© Young Minds Smart Learning Platform</div>
      </div>

      <div className="flex flex-col justify-center p-6 sm:p-12">
        <div className="lg:hidden mb-6"><Logo /></div>
        <div className="max-w-md w-full mx-auto">
          <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600 mb-6">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Forgot your password?</h1>
          <p className="mt-2 text-slate-600">No problem. Enter your email and we'll send you a reset link.</p>

          {!done ? (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <label className="text-sm font-semibold text-slate-700">Email</label>
                <div className="mt-1.5 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email"
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                  />
                </div>
              </div>
              {err && <div data-testid="forgot-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">{err}</div>}
              <button type="submit" disabled={submitting} data-testid="forgot-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60">
                {submitting ? "Sending..." : "Send reset link"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          ) : (
            <div className="mt-8 space-y-4" data-testid="forgot-success">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-emerald-900">
                <h3 className="font-heading font-bold text-lg">Check your inbox</h3>
                <p className="mt-1 text-sm">If an account exists for <span className="font-semibold">{email}</span>, you'll receive a reset link within a minute. The link expires in 1 hour.</p>
              </div>

              {resetLink && (
                <div data-testid="forgot-mock-link" className="rounded-xl bg-amber-50 border border-amber-200 p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">Email is MOCKED in dev</p>
                  <p className="text-xs text-amber-900 mb-3">Add a <code>RESEND_API_KEY</code> to backend <code>.env</code> for real email. For now, click the dev link below to continue:</p>
                  <a href={resetLink} className="text-sm font-semibold text-blue-700 underline break-all">{resetLink}</a>
                </div>
              )}

              <Link to="/login" className="block text-center text-sm font-semibold text-blue-600 hover:underline">Return to sign in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
